import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PropertyCard from "../components/PropertyCard";
import { fetchInternalProperties, fetchMLSAll } from "../services/propertyService";
import { Search, SlidersHorizontal, X, Bed, Bath, Hash, Loader2 } from "lucide-react";

export default function Properties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "all";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 20000000 });
  const [beds, setBeds] = useState("any");
  const [baths, setBaths] = useState("any");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const normalizeProperty = (p, source = "internal") => {
    if (source === "mls") {
      const rawPrice = Number(p.ListPrice || p.Price || 0);
      return {
        id: p.card_id || p.mls_number || p.id,
        title: p.Name || p.title || "Exclusive St. John Property",
        price: rawPrice ? `$${rawPrice.toLocaleString()}` : (p.ui_price || "$0"),
        rawPrice: rawPrice,
        location: p.address_line1 ? `${p.address_line1}, ${p.City || "St. John"}` : (p.City || "St. John, USVI"),
        image: p.images?.[0] || p.card_image || "https://images.unsplash.com/photo-1512917774080-9991f1c4c750",
        beds: p.BedsTotal || p.beds || "0",
        baths: p.BathsTotal || p.baths || "0",
        sqft: p.lot_size_acres ? `${p.lot_size_acres} Acres` : (p.lot_size_sqft ? `${p.lot_size_sqft} SqFt` : "N/A"),
        type: p.PropertyType || "Residential",
        subcategory: (p.PropertyType || "Other").toLowerCase(),
        status: p.status || "Active",
        slug: p.card_id || (p.mls_number ? p.mls_number.replace("#", "") : String(p.id)),
        source: "mls"
      };
    }

    const rawPrice = Number(p.price || 0);
    return {
      id: p.id,
      title: p.title,
      price: rawPrice ? `$${rawPrice.toLocaleString()}` : "$0",
      rawPrice: rawPrice,
      location: p.address || "St. John, USVI",
      image: p.images?.[0] || "https://images.unsplash.com/photo-1512917774080-9991f1c4c750",
      beds: p.beds || "0",
      baths: p.baths || "0",
      sqft: p.square_feet || "N/A",
      type: p.property_type || "Residential",
      subcategory: (p.subcategory || p.property_type || "Other").toLowerCase(),
      status: p.listing_type || "Active",
      slug: p.slug || String(p.id),
      source: "internal"
    };
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [internalRaw, mlsRaw] = await Promise.all([
          fetchInternalProperties(),
          fetchMLSAll()
        ]);

        const filteredInternal = (internalRaw || []).filter(
          (p) => p.property_type?.toLowerCase() !== "commercial"
        );
        const normalizedInternal = filteredInternal.map((p) => normalizeProperty(p, "internal"));
        
        const normalizedMLS = (mlsRaw || []).map((p) => normalizeProperty(p, "mls"));

        // Combine and de-duplicate by slug/id
        const combined = [...normalizedInternal];
        const existingSlugs = new Set(combined.map((c) => c.slug));

        normalizedMLS.forEach((item) => {
          if (!existingSlugs.has(item.slug)) {
            combined.push(item);
          }
        });

        setProperties(combined);
      } catch (error) {
        console.error("Failed to load properties:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const getFilteredProperties = () => {
    let result = properties;

    // Tab Filtering
    if (activeTab === "sold") {
      result = result.filter((p) => p.status?.toLowerCase() === "sold");
    } else {
      // Exclude Sold from active tabs
      result = result.filter((p) => p.status?.toLowerCase() !== "sold");

      if (activeTab === "residential") {
        result = result.filter((p) => {
          const type = p.type?.toLowerCase() || "";
          return (
            type === "residential" ||
            type === "house" ||
            type === "villa" ||
            type === "cottage" ||
            type === "combo"
          );
        });
      } else if (activeTab === "land") {
        result = result.filter((p) => p.type?.toLowerCase() === "land");
      }
    }

    // Text Search Query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title?.toLowerCase().includes(q) ||
          p.location?.toLowerCase().includes(q)
      );
    }

    // Price Filter
    if (priceRange.min > 0) {
      result = result.filter((p) => p.rawPrice >= priceRange.min);
    }
    if (priceRange.max < 20000000) {
      result = result.filter((p) => p.rawPrice <= priceRange.max);
    }

    // Beds Filter
    if (beds !== "any") {
      result = result.filter((p) => Number(p.beds) >= Number(beds));
    }

    // Baths Filter
    if (baths !== "any") {
      result = result.filter((p) => Number(p.baths) >= Number(baths));
    }

    return result;
  };

  const filtered = getFilteredProperties();

  // Group by subcategory for display
  const grouped = filtered.reduce((acc, prop) => {
    const sub = prop.subcategory || "featured";
    if (!acc[sub]) acc[sub] = [];
    acc[sub].push(prop);
    return acc;
  }, {});

  const tabs = [
    { id: "all", label: "All Exclusives" },
    { id: "residential", label: "Residential" },
    { id: "land", label: "Land" },
    { id: "sold", label: "Recently Sold" }
  ];

  return (
    <div className="bg-[#FAF6F0] min-h-screen font-sans flex flex-col">
      <Navbar isTransparent={false} />

      {/* Header Banner */}
      <section className="pt-24 lg:pt-32 pb-16 bg-ink-900 text-white relative overflow-hidden px-6 md:px-12">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20"
          >
            <span className="w-2 h-2 rounded-full bg-tropical-400 animate-pulse"></span>
            <span className="text-xs font-semibold text-white tracking-widest uppercase font-display">
              St. John Real Estate Portfolio
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-tight font-display mb-6"
          >
            Featured Properties & <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-tropical-300 to-white/70">
              Island Collection.
            </span>
          </motion.h1>
          <p className="text-ink-400 text-base md:text-lg max-w-2xl font-light">
            Discover premier luxury villas, pristine land parcels, and exclusive real estate listings across St. John, USVI.
          </p>
        </div>
      </section>

      {/* Sticky Tab & Search Filter Bar */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#E8E3DC] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-4">
          
          {/* Filter Tabs */}
          <div className="flex gap-2 sm:gap-6 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-full transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-brand-dark text-white shadow-sm"
                    : "text-ink-600 hover:bg-gray-100 hover:text-ink-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Box & Filter Button */}
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="relative flex-grow md:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input
                type="text"
                placeholder="Search location or title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-xs text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-tropical-500/20 focus:border-tropical-500 transition-all font-light"
              />
            </div>

            <button
              onClick={() => setIsFilterOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand-dark hover:bg-brand-dark/95 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm transition-all shrink-0 cursor-pointer"
            >
              <SlidersHorizontal className="w-4 h-4" /> Filter
            </button>
          </div>
        </div>
      </div>

      {/* Main Listing Section */}
      <section className="py-16 px-6 md:px-12 flex-grow">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="h-96 flex flex-col items-center justify-center gap-4">
              <Loader2 className="w-10 h-10 text-tropical-500 animate-spin" />
              <p className="text-sm font-medium text-ink-600 font-display">Loading St. John listings...</p>
            </div>
          ) : Object.keys(grouped).length > 0 ? (
            Object.entries(grouped).map(([subcategory, items]) => (
              <div key={subcategory} className="mb-16 last:mb-0">
                {/* Category Header */}
                <div className="flex items-center justify-between mb-8 pb-3 border-b border-[#E8E3DC]">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-tropical-500 font-display">
                      Property Category
                    </p>
                    <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 font-display capitalize">
                      {subcategory}
                    </h2>
                  </div>
                  <span className="text-xs font-semibold text-ink-500 bg-white px-3.5 py-1.5 rounded-full border border-[#E8E3DC]">
                    {items.length} {items.length === 1 ? "Listing" : "Listings"}
                  </span>
                </div>

                {/* Property Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {items.map((prop) => (
                    <PropertyCard key={prop.id} {...prop} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-24 bg-white rounded-3xl border border-[#E8E3DC] shadow-sm max-w-2xl mx-auto px-6">
              <div className="w-16 h-16 bg-[#FAF8F5] rounded-full flex items-center justify-center mx-auto mb-6 border border-[#E8E3DC]">
                <Search className="w-6 h-6 text-ink-400" />
              </div>
              <h3 className="text-2xl font-bold text-ink-900 font-display mb-2">
                No properties found
              </h3>
              <p className="text-ink-600 text-sm font-light leading-relaxed mb-6">
                We couldn't find any listings matching your search criteria. Try adjusting your filters.
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setActiveTab("all");
                  setPriceRange({ min: 0, max: 20000000 });
                  setBeds("any");
                  setBaths("any");
                }}
                className="px-6 py-3 bg-brand-dark text-white text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-brand-dark/95 transition-all shadow-md cursor-pointer"
              >
                Reset All Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Filter Drawer Slide-over */}
      <AnimatePresence>
        {isFilterOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsFilterOpen(false)}
              className="fixed inset-0 bg-ink-900/60 backdrop-blur-sm z-[100]"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[110] p-8 shadow-2xl overflow-y-auto font-sans"
            >
              <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-100">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-tropical-500 font-display">
                    Refine Search
                  </h4>
                  <h2 className="text-2xl font-bold text-ink-900 font-display">Filter Properties</h2>
                </div>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-ink-600 hover:bg-ink-900 hover:text-white transition-all cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-8">
                {/* Price Range */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Hash className="w-4 h-4 text-tropical-500" />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-ink-900">
                      Price Range ($)
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] uppercase font-bold text-ink-500 mb-1 block">Min Price</label>
                      <input
                        type="number"
                        placeholder="0"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange((prev) => ({ ...prev, min: Number(e.target.value) }))}
                        className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-sm text-ink-900 focus:outline-none focus:border-tropical-500 font-light"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold text-ink-500 mb-1 block">Max Price</label>
                      <input
                        type="number"
                        placeholder="20000000"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange((prev) => ({ ...prev, max: Number(e.target.value) }))}
                        className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-sm text-ink-900 focus:outline-none focus:border-tropical-500 font-light"
                      />
                    </div>
                  </div>
                </div>

                {/* Min Bedrooms */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Bed className="w-4 h-4 text-tropical-500" />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-ink-900">
                      Minimum Bedrooms
                    </h4>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {["any", "1", "2", "3", "4", "5+"].map((num) => (
                      <button
                        key={num}
                        onClick={() => setBeds(num)}
                        className={`px-4 py-2.5 text-xs font-bold rounded-xl transition-all border cursor-pointer ${
                          beds === num
                            ? "bg-brand-dark text-white border-brand-dark shadow-sm"
                            : "bg-[#FAF8F5] text-ink-600 border-[#E8E3DC] hover:border-tropical-500"
                        }`}
                      >
                        {num.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Min Bathrooms */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Bath className="w-4 h-4 text-tropical-500" />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-ink-900">
                      Minimum Bathrooms
                    </h4>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {["any", "1", "2", "3", "4+"].map((num) => (
                      <button
                        key={num}
                        onClick={() => setBaths(num)}
                        className={`px-4 py-2.5 text-xs font-bold rounded-xl transition-all border cursor-pointer ${
                          baths === num
                            ? "bg-brand-dark text-white border-brand-dark shadow-sm"
                            : "bg-[#FAF8F5] text-ink-600 border-[#E8E3DC] hover:border-tropical-500"
                        }`}
                      >
                        {num.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-6 border-t border-gray-100 flex flex-col gap-3">
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="w-full py-4 bg-brand-dark hover:bg-brand-dark/95 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md cursor-pointer"
                >
                  Show {filtered.length} Results
                </button>
                <button
                  onClick={() => {
                    setBeds("any");
                    setBaths("any");
                    setPriceRange({ min: 0, max: 20000000 });
                  }}
                  className="w-full py-2.5 text-xs font-bold uppercase tracking-wider text-ink-500 hover:text-ink-900 transition-colors cursor-pointer"
                >
                  Reset Defaults
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
